import React from 'react';
import { Helmet } from 'react-helmet';

// Organization Schema - For homepage
export function OrganizationSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'The Poetic Method',
    url: 'https://thepoeticmethod.com',
    logo: 'https://thepoeticmethod.com/logo.png',
    description: 'Healthcare organizational consulting integrating expressive arts and evidence-based frameworks for institutional transformation.',
    founder: {
      '@type': 'Person',
      name: 'Dr. Michelande Ridoré',
      jobTitle: 'Founder & CEO',
      url: 'https://thepoeticmethod.com/about',
    },
    sameAs: [
      'https://linkedin.com/in/michelanderidore',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Consulting Inquiries',
      email: 'poeticmethod101@gmail.com',
      availableLanguage: ['English'],
    },
    areaServed: {
      '@type': 'Country',
      name: 'United States',
    },
    industry: 'Healthcare Consulting',
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// Person Schema - For About page
export function PersonSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Dr. Michelande Ridoré',
    jobTitle: 'Founder & CEO of The Poetic Method',
    description: 'Healthcare organizational consultant with 15+ years of executive leadership experience in academic health systems. Expert in improvement science, expressive arts, and institutional transformation.',
    url: 'https://thepoeticmethod.com/about',
    image: 'https://thepoeticmethod.com/assets/images/cover.jpg',
    alumniOf: {
      '@type': 'EducationalOrganization',
      name: 'University of Miami',
    },
    hasCredential: 'Doctor of Education in Applied Learning Sciences',
    knowsAbout: [
      'Healthcare Organizational Consulting',
      'Improvement Science',
      'Expressive Arts Therapy',
      'Leadership Development',
      'High-Reliability Organizations',
      'Applied Learning Sciences',
      'Organizational Culture',
      'Patient Safety',
    ],
    sameAs: [
      'https://linkedin.com/in/michelanderidore',
    ],
    worksFor: {
      '@type': 'Organization',
      name: 'The Poetic Method',
      url: 'https://thepoeticmethod.com',
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// Professional Service Schema - For Contact page
export function ProfessionalServiceSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'The Poetic Method',
    url: 'https://thepoeticmethod.com/contact',
    description: 'Strategic consulting partnerships for healthcare organizations.',
    priceRange: 'By consultation',
    provider: {
      '@type': 'Person',
      name: 'Dr. Michelande Ridoré',
    },
    areaServed: {
      '@type': 'Country',
      name: 'United States',
    },
    serviceType: 'Healthcare Organizational Consulting',
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// Service Schema - For Partnerships page
export function ServiceSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: [
      {
        '@type': 'Service',
        name: 'Culture Catalyst Workshop',
        description: 'Half-day or full-day interactive workshop blending expressive arts with evidence-based frameworks for team development.',
        provider: {
          '@type': 'Organization',
          name: 'The Poetic Method',
        },
        areaServed: 'United States',
        audience: {
          '@type': 'Audience',
          audienceType: 'Healthcare Institutions',
        },
      },
      {
        '@type': 'Service',
        name: 'Leadership Accelerator Program',
        description: '6-week cohort-based program combining leadership development, coaching, and applied learning sciences.',
        provider: {
          '@type': 'Organization',
          name: 'The Poetic Method',
        },
        areaServed: 'United States',
        audience: {
          '@type': 'Audience',
          audienceType: 'Healthcare Leaders',
        },
      },
      {
        '@type': 'Service',
        name: 'Operational Excellence Project',
        description: '8-12 week engagement using improvement science to address operational and process bottlenecks.',
        provider: {
          '@type': 'Organization',
          name: 'The Poetic Method',
        },
        areaServed: 'United States',
        audience: {
          '@type': 'Audience',
          audienceType: 'Healthcare Organizations',
        },
      },
      {
        '@type': 'Service',
        name: 'Executive Advisory',
        description: 'High-trust thought partnership for senior leaders navigating institutional responsibility.',
        provider: {
          '@type': 'Organization',
          name: 'The Poetic Method',
        },
        areaServed: 'United States',
        audience: {
          '@type': 'Audience',
          audienceType: 'Healthcare Executives',
        },
      },
    ],
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// FAQ Schema - For Partnerships page
export function FAQSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What services does The Poetic Method offer?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The Poetic Method offers Culture Catalyst Workshops, Leadership Accelerator Programs, Operational Excellence Projects, Culture Assessments, Executive Advisory, and Transformation Partnerships for healthcare organizations.',
        },
      },
      {
        '@type': 'Question',
        name: 'Who is Dr. Michelande Ridoré?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Dr. Michelande Ridoré is the Founder & CEO of The Poetic Method, with over 15 years of executive leadership experience in major academic health systems. She holds a Doctor of Education in Applied Learning Sciences from the University of Miami.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is The Poetic Method approach?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The Poetic Method integrates improvement science with expressive arts, combining data-driven analysis with human-centered approaches to drive measurable outcomes in healthcare organizations.',
        },
      },
      {
        '@type': 'Question',
        name: 'How can I contact The Poetic Method?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'You can contact The Poetic Method through the contact form on our website at thepoeticmethod.com/contact or email us directly at poeticmethod101@gmail.com. We typically respond within 1-2 business days.',
        },
      },
    ],
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// Breadcrumb Schema - For all pages
export function BreadcrumbSchema({ items }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// WebPage Schema - Generic page schema
export function WebPageSchema({ title, description, url, lastReviewed }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description: description,
    url: url,
    ...(lastReviewed && { lastReviewed: lastReviewed }),
    inLanguage: 'en-US',
    isPartOf: {
      '@type': 'WebSite',
      name: 'The Poetic Method',
      url: 'https://thepoeticmethod.com',
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}
